/**
 * COL-508: how one incoming bank transfer is split across a tenancy's rent.
 *
 * The browser mirror of `decide_reconciliation_split`
 * (supabase/migrations/20260930120200_reconciliation_transfer_split.sql), used
 * only to PREVIEW the split in the reconciliation queue before an admin
 * confirms or assigns. The database is the authority: it returns what it
 * actually booked, and that — not this prediction — is what the toast reports.
 * Keep the two in step; `transfer-split.test.ts` pins the shared rule.
 *
 * The rule (Neel, 2026-09-29):
 *   1. the tenancy's oldest open rent,
 *   2. then its Mahngebühren (the tenancy's one fee pot — fees are not
 *      recorded per month, and the pot is what the oldest overdue month has
 *      been accruing),
 *   3. then the other open rents, oldest to newest,
 *   4. then credit on the next rent (`creditNext`), or a remainder that stays
 *      in the queue.
 * The invoice an admin picks identifies the TENANCY; it does not jump the queue.
 */

import { isOpenRentStatus } from "./rent";
import { formatCents } from "./money";

/** Cents still owed on a rent row: what a transfer is measured against (COL-417). */
export function openBalanceCents(
  p:
    | { amount_cents?: number | null; received_cents?: number | null }
    | null
    | undefined
): number {
  if (!p) return 0;
  return Math.max(0, (p.amount_cents ?? 0) - (p.received_cents ?? 0));
}

/** A rent row as the split sees it. Structural, so queue candidates and open invoices both fit. */
export type SplitRentRow = {
  id: string;
  booking_id: number | null;
  due_date: string | null;
  amount_cents: number;
  received_cents?: number | null;
  status: string;
  /** Invoice kind; absent is treated as rent so existing callers are unchanged. A deposit never joins the cascade. */
  type?: string | null;
  stripe_payment_intent_id?: string | null;
  stripe_charge_id?: string | null;
};

/** A card/SEPA charge is attached: the gateway owns this month and collects it in full. */
export function hasChargeInFlight(row: SplitRentRow): boolean {
  return !!(row.stripe_payment_intent_id || row.stripe_charge_id);
}

/**
 * COL-508: which invoice a reconciliation row should be READ as pointing at.
 *
 * `reconciliation_matches.payment_id` is half of
 * `reconciliation_matches_tx_payment_uidx`, so an assign onto an invoice that a
 * sibling row of the same transfer already names cannot move it — the decision
 * records its real target in `decided_payment_id` instead. On an undecided row,
 * and on anything decided before COL-508, that is NULL and this is the
 * candidate pointer, exactly as before.
 *
 * Structural on purpose: it takes only the two embeds, so it lives here rather
 * than in the hook module (which constructs the Supabase client at import
 * time) and both the page and its tests can reach it.
 */
export function decisionPayment<T>(row: {
  payments?: T | null;
  decided_payments?: T | null;
}): T | null {
  return row.decided_payments ?? row.payments ?? null;
}

export type TransferSplitRentLine = {
  payment_id: string;
  due_date: string | null;
  /** Rent cents this transfer books on the month (excludes any credit riding on it). */
  cents: number;
  settles: boolean;
  open_after_cents: number;
};

export type TransferSplit = {
  rent: TransferSplitRentLine[];
  fee_cents: number;
  /** Fees still owed on the tenancy after this transfer. */
  fees_owed_after_cents: number;
  credit_cents: number;
  remainder_cents: number;
  /** What this decision books (everything except the remainder). */
  applied_cents: number;
  /** Open months left out because a card/SEPA charge is already collecting them. */
  skipped_in_flight: { payment_id: string; due_date: string | null }[];
};

export type FeePortionError = "exceeds_transfer" | "exceeds_owed";

/** Why an admin-stated fee portion would be refused — the RPC's own checks, in its order. */
export function feePortionError(
  feeCents: number | null,
  availableCents: number,
  feesOwedCents: number
): FeePortionError | null {
  if (feeCents == null) return null;
  if (feeCents > availableCents) return "exceeds_transfer";
  if (feeCents > feesOwedCents) return "exceeds_owed";
  return null;
}

function byDueDateThenId(a: SplitRentRow, b: SplitRentRow): number {
  // NULLS LAST, then id — the SQL's `ORDER BY due_date ASC NULLS LAST, id ASC`.
  const da = a.due_date ?? "￿";
  const db = b.due_date ?? "￿";
  if (da !== db) return da < db ? -1 : 1;
  return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
}

export function planTransferSplit(input: {
  /** The part of the transfer this queue row is about. */
  availableCents: number;
  /** The confirmed/assigned invoice — identifies the tenancy. */
  anchor: SplitRentRow;
  /** Candidate rows; filtered here to the anchor's tenancy. */
  rows: readonly SplitRentRow[];
  feesOwedCents: number;
  /** An admin-stated fee portion, or null for the rule. */
  feeOverrideCents: number | null;
  creditNext: boolean;
  /**
   * Months this transfer has ALREADY paid, which the RPC skips:
   *
   *   AND NOT EXISTS (SELECT 1 FROM payment_receipts r
   *                   WHERE r.payment_id = p.id
   *                     AND r.bank_transaction_id = v_tx.id
   *                     AND r.applies_to = 'rent')
   *
   * Only reachable on a second decision of one transfer — assigning a
   * remainder row — which COL-508 made ordinary. Omitted (a first decision has
   * placed nothing yet) it changes nothing.
   */
  excludePaymentIds?: readonly string[];
}): TransferSplit {
  const available = Math.max(0, Math.round(input.availableCents));
  const feesOwed = Math.max(0, Math.round(input.feesOwedCents));
  const override =
    input.feeOverrideCents == null
      ? null
      : Math.max(0, Math.round(input.feeOverrideCents));

  const tenancy = new Map<string, SplitRentRow>();
  for (const r of [input.anchor, ...input.rows]) {
    const sameTenancy =
      r.id === input.anchor.id ||
      (input.anchor.booking_id != null &&
        r.booking_id === input.anchor.booking_id);
    if (sameTenancy && !tenancy.has(r.id)) tenancy.set(r.id, r);
  }
  const open = [...tenancy.values()]
    .filter(
      (r) =>
        // A deposit never joins the rent cascade (it books via its own path).
        (r.type ?? "rent") !== "deposit" &&
        isOpenRentStatus(r.status) &&
        openBalanceCents(r) > 0
    )
    .sort(byDueDateThenId);
  const alreadyPaidByThisTransfer = new Set(input.excludePaymentIds ?? []);
  const eligible = open.filter(
    (r) => !hasChargeInFlight(r) && !alreadyPaidByThisTransfer.has(r.id)
  );
  const skipped = open
    .filter(hasChargeInFlight)
    .map((r) => ({ payment_id: r.id, due_date: r.due_date }));

  let fee = override ?? 0;
  let pool = available - fee;
  const planned: { row: SplitRentRow; cents: number }[] = [];

  for (const [i, row] of eligible.entries()) {
    if (pool <= 0) break;
    const openCents = openBalanceCents(row);
    const take = Math.min(pool, openCents);
    planned.push({ row, cents: take });
    pool -= take;
    // The oldest month's Mahngebühren follow its rent, once that rent is covered.
    if (i === 0 && override == null && take === openCents) {
      fee = Math.min(feesOwed, pool);
      pool -= fee;
    }
  }
  if (planned.length === 0 && override == null) {
    fee = Math.min(feesOwed, pool);
    pool -= fee;
  }

  let credit = 0;
  let remainder = 0;
  if (pool > 0) {
    if (input.creditNext && planned.length > 0) credit = pool;
    else remainder = pool;
  }

  const rent = planned.map(({ row, cents }) => {
    const openAfter = Math.max(0, openBalanceCents(row) - cents);
    return {
      payment_id: row.id,
      due_date: row.due_date,
      cents,
      settles: openAfter === 0,
      open_after_cents: openAfter,
    };
  });

  return {
    rent,
    fee_cents: fee,
    fees_owed_after_cents: Math.max(0, feesOwed - fee),
    credit_cents: credit,
    remainder_cents: remainder,
    applied_cents: available - remainder,
    skipped_in_flight: skipped,
  };
}

/**
 * The split as the RPC returns it (`decide_reconciliation_split`'s jsonb).
 * Parsed defensively: an older database returns nothing at all.
 */
export type BookedTransferSplit = {
  applied_cents: number;
  fee_cents: number;
  rent: TransferSplitRentLine[];
  credit_cents: number;
  remainder_cents: number;
};

export function parseBookedSplit(raw: unknown): BookedTransferSplit | null {
  if (!raw || typeof raw !== "object") return null;
  const o = raw as Record<string, unknown>;
  const num = (v: unknown) => (Number.isFinite(Number(v)) ? Number(v) : 0);
  const rent = Array.isArray(o.rent)
    ? o.rent.map((r) => {
        const x = (r ?? {}) as Record<string, unknown>;
        return {
          payment_id: String(x.payment_id ?? ""),
          due_date: x.due_date == null ? null : String(x.due_date),
          cents: num(x.cents),
          settles: x.settled === true,
          open_after_cents: num(x.open_after_cents),
        };
      })
    : [];
  return {
    applied_cents: num(o.applied_cents),
    fee_cents: num(o.fee_cents),
    rent,
    credit_cents: num(o.credit_cents),
    remainder_cents: num(o.remainder_cents),
  };
}

/** "September 2026" — the month a split line pays. */
export function periodLabel(
  dateish: string | null | undefined,
  language: string
): string {
  if (!dateish) return "—";
  const d = new Date(`${String(dateish).slice(0, 10)}T12:00:00`);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString(language === "de" ? "de-DE" : "en-GB", {
    month: "long",
    year: "numeric",
  });
}

/** One line per thing a split books — the toast and the Confirm hint. */
export function splitSummary(
  split: {
    rent: TransferSplitRentLine[];
    fee_cents: number;
    credit_cents: number;
    remainder_cents: number;
  },
  currency: string,
  t: (key: string, fallback?: string) => string,
  language: string
): string {
  const money = (c: number) => formatCents(c, currency);
  const fee = t("admin.reconciliation.split.partFee", "Mahngebühren {amount}");
  const parts: string[] = [];
  split.rent.forEach((r, i) => {
    parts.push(
      (r.settles
        ? t(
            "admin.reconciliation.split.partRentPaid",
            "{month} {amount} — paid"
          )
        : t(
            "admin.reconciliation.split.partRentOpen",
            "{month} {amount} — {open} still open"
          )
      )
        .replace("{month}", periodLabel(r.due_date, language))
        .replace("{amount}", money(r.cents))
        .replace("{open}", money(r.open_after_cents))
    );
    if (i === 0 && split.fee_cents > 0)
      parts.push(fee.replace("{amount}", money(split.fee_cents)));
  });
  if (split.rent.length === 0 && split.fee_cents > 0)
    parts.push(fee.replace("{amount}", money(split.fee_cents)));
  if (split.credit_cents > 0)
    parts.push(
      t(
        "admin.reconciliation.split.partCredit",
        "{amount} credit for the next rent"
      ).replace("{amount}", money(split.credit_cents))
    );
  if (split.remainder_cents > 0)
    parts.push(
      t(
        "admin.reconciliation.split.partRemainder",
        "{amount} left in the queue"
      ).replace("{amount}", money(split.remainder_cents))
    );
  return parts.join(" · ");
}
